import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { LoadConfigResponse, ThemeSection } from '../../features/auth/models/search-response.model';
import { environment } from '../../../environments/environment';


@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly storageKey = 'app-theme';

  constructor(private http: HttpClient) {}

  /** Consulta al backend el tema según id de cuenta */
  loadThemeConfig(idCuenta: number): Observable<LoadConfigResponse> {
    const url = `${this.baseUrl}/load/${idCuenta}/configuration`;
    return this.http.get<LoadConfigResponse>(url);
  }

  /** Aplica el tema al :root y lo guarda en localStorage */
  applyTheme(theme: ThemeSection[]): void {
    const root = document.documentElement;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isIOSPWA = isIOS && (window.navigator as any).standalone;
    
    const applyStyles = () => {
      theme.forEach((t) => {
        root.style.setProperty(`--${t.section}`, t.color);
      });
    };
    
    // Aplicar estilos inmediatamente
    applyStyles();

    localStorage.setItem(this.storageKey, JSON.stringify(theme));
    
    // Para iOS PWA, usar estrategia más agresiva
    if (isIOSPWA || isIOS) {
      // Crear/actualizar style tag con !important
      let styleTag = document.getElementById('ios-theme-fix');
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = 'ios-theme-fix';
        document.head.appendChild(styleTag);
      }
      
      let cssRules = ':root {';
      theme.forEach((t) => {
        cssRules += `--${t.section}: ${t.color} !important;`;
      });
      cssRules += '}';
      
      styleTag.textContent = cssRules;
      
      // Múltiples intentos de aplicación
      const delays = [50, 100, 200, 500, 1000, 2000];
      delays.forEach(delay => {
        setTimeout(() => {
          applyStyles();
          styleTag!.textContent = cssRules;
        }, delay);
      });
      
      // Listener para cuando la app se vuelve visible
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          setTimeout(() => {
            applyStyles();
            styleTag!.textContent = cssRules;
          }, 100);
        }
      };
      
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }
  }

  /** Carga el tema desde localStorage (si existe) */
  loadFromStorage(): void {
    const saved = localStorage.getItem(this.storageKey);
    if (saved) {
      try {
        const theme = JSON.parse(saved) as ThemeSection[];

        
        // Usar el método completo que incluye todas las estrategias para iOS
        this.applyTheme(theme);
      } catch (e) {
        console.error('Error al cargar el tema desde localStorage', e);
      }
    }
  }

  /** Limpia el tema almacenado */
  clearTheme(): void {
    localStorage.removeItem(this.storageKey);
  }
  
  /** Método específico para iOS PWA que fuerza la aplicación del tema */
  forceApplyThemeForIOSPWA(theme: ThemeSection[]): void {
    const urlParams = new URLSearchParams(window.location.search);
    const testIOSPWA = urlParams.get('test-ios-pwa') === 'true';
    const isIOSPWA = (/iPad|iPhone|iPod/.test(navigator.userAgent) && (window.navigator as any).standalone) || testIOSPWA;
    
    if (!isIOSPWA) {
      this.applyTheme(theme);
      return;
    }
    

    const root = document.documentElement;
    
    // Estrategia 1: Variables CSS normales
    const applyNormalStyles = () => {
      theme.forEach((t) => {
        root.style.setProperty(`--${t.section}`, t.color);
      });
    };
    
    // Estrategia 2: Style tag con !important
    const applyStyleTag = () => {
      let styleTag = document.getElementById('ios-pwa-theme-force');
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = 'ios-pwa-theme-force';
        styleTag.setAttribute('data-ios-pwa', 'true');
        document.head.appendChild(styleTag);
      }
      
      let cssRules = ':root {';
      theme.forEach((t) => {
        cssRules += `--${t.section}: ${t.color} !important;`;
      });
      cssRules += '} * { --webkit-transform: translateZ(0); }';
      
      styleTag.textContent = cssRules;
    };
    
    // Estrategia 3: Clases CSS específicas
    const applyClassStyles = () => {
      let classStyleTag = document.getElementById('ios-pwa-class-theme');
      if (!classStyleTag) {
        classStyleTag = document.createElement('style');
        classStyleTag.id = 'ios-pwa-class-theme';
        document.head.appendChild(classStyleTag);
      }
      
      let classCss = '';
      theme.forEach((t) => {
        classCss += `.theme-${t.section} { color: ${t.color} !important; background-color: ${t.color} !important; border-color: ${t.color} !important; }`;
      });
      
      classStyleTag.textContent = classCss;
    };
    
    // Aplicar todas las estrategias inmediatamente
    applyNormalStyles();
    applyStyleTag();
    applyClassStyles();
    
    // Guardar en localStorage
    localStorage.setItem(this.storageKey, JSON.stringify(theme));
    
    // Aplicar múltiples veces con diferentes delays
    const delays = [50, 100, 200, 500, 1000, 2000, 3000];
    delays.forEach(delay => {
      setTimeout(() => {
        applyNormalStyles();
        applyStyleTag();

      }, delay);
    });
    
    // Listener para eventos de visibilidad
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setTimeout(() => {
          applyNormalStyles();
          applyStyleTag();

        }, 100);
      }
    };
    
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listener para cuando la app se enfoca
    const handleFocus = () => {
      setTimeout(() => {
        applyNormalStyles();
        applyStyleTag();

      }, 100);
    };
    
    window.removeEventListener('focus', handleFocus);
    window.addEventListener('focus', handleFocus);
  }
}